import React from "react";
import { cn } from "./cn";
import { Select } from './Field';

/**
 * Bun activity timeline: dot + hairline connector, strong event title with
 * stone meta, slate body. The newest (or active) item gets a blue dot.
 *
 *   <Timeline>
 *     <Timeline.Item active title="Meeting note" meta="Alex R, today 9:48">...</Timeline.Item>
 *     <Timeline.Item title="Task completed" meta="system, yesterday">...</Timeline.Item>
 *   </Timeline>
 */
export default function Timeline({ className, children }) {
  const items = React.Children.toArray(children);
  return (
    <div className={cn("flex flex-col font-instrument", className)}>
      {items.map((child, i) =>
        React.cloneElement(child, { isLast: i === items.length - 1, key: child.key ?? i })
      )}
    </div>
  );
}

function Item({ active, title, meta, isLast, compactOnMobile = false, className, children }) {
  return (
    <div className={cn("flex gap-3", className)}>
      <div className="flex flex-col items-center">
        <span
          className={cn(
            "w-[9px] h-[9px] rounded-full mt-1 shrink-0",
            active ? "bg-bun-blue" : "bg-bun-dim"
          )}
        />
        {!isLast && <span className="w-[1.5px] flex-1 bg-bun-line" />}
      </div>
      <div className={cn("min-w-0", !isLast && (compactOnMobile ? "sm:pb-4" : "pb-4"))}>
        <div className="text-bun-body">
          <span className="font-strong text-bun-text">{title}</span>
          {meta && <span className="font-book text-bun-stone"> · {meta}</span>}
        </div>
        {children && (
          <div className="text-[12.5px] font-book text-bun-slate leading-relaxed mt-[3px]">
            {children}
          </div>
        )}
      </div>
    </div>
  );
}

Timeline.Item = Item;

/** Proportional, possibly overlapping intervals; mobile uses the same selection. */
function Rail({ items, value, onChange, label = 'Timeline' }) {
  return <div>
    <div className="sm:hidden">
      <Select aria-label={label} value={value || ''} onChange={event => onChange(event.target.value)}>
        <option value="" disabled>Select an event</option>
        {items.map(item => <option key={item.id} value={item.id}>{item.label}</option>)}
      </Select>
    </div>
    <div role="group" aria-label={label} className="relative hidden h-11 rounded-bun border border-bun-line bg-bun-mist sm:block">
      {items.map(item => <button key={item.id} type="button" aria-label={item.label}
        aria-pressed={item.id === value} title={item.label} onClick={() => onChange(item.id)}
        className={cn('absolute inset-y-1 min-w-[3px] rounded-bun-sm border-0 opacity-70 hover:opacity-100 focus-visible:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-bun-blue', item.id === value && 'z-10 opacity-100 ring-2 ring-bun-ink')}
        style={{ left: `${item.start}%`, width: `${item.width}%`, backgroundColor: item.color }} />)}
    </div>
  </div>;
}
Timeline.Rail = Rail;
